if (!customElements.get("quiz-promotion")) {
  customElements.define(
    "quiz-promotion",
    class QuizPromotion extends HTMLElement {
      static QUIZ_PROPERTY = "quiz_product";
      static QUIZ_TAG = "quiz_product";
      static GIFT_FLAG_PROPERTY = "_quiz_promotional_product";
      static GIFT_TAG = "quiz_promotional_product";
      static STORAGE_KEY = "quiz-promotion-dismissed";
      static ENTITLED_KEY = "quiz-promotion-entitled";
      static PROMPT_DISMISS_KEY = "quiz-promotion-prompt-dismissed";
      static MIN_QUIZ_PRODUCTS = 2;

      constructor() {
        super();
        this.isOpen = false;
        this.isBusy = false;
        this.removingFree = false;
        this.lockingQty = false;
        this.initialized = false;
        this.evaluateTimer = null;
        this.lastCart = null;
        this.lastEntitledCount = 0;
        this.pendingCartChange = false;
        this.productTagsCache = {};
        this.cart =
          document.querySelector("cart-drawer") ||
          document.querySelector("cart-notification");
        this.overlay = this.querySelector("[data-quiz-promotion-overlay]");
        this.dialog = this.querySelector("[data-quiz-promotion-dialog]");
        this.closeButtons = this.querySelectorAll("[data-quiz-promotion-close]");
        this.addButtons = this.querySelectorAll("[data-quiz-promotion-add]");
        this.teaser = this.querySelector("[data-quiz-promotion-teaser]");
        this.teaserDismiss = this.querySelector("[data-quiz-promotion-teaser-dismiss]");
        this.teaserReopen = this.querySelector("[data-quiz-promotion-teaser-reopen]");
      }

      connectedCallback() {
        if (this.dataset.enabled !== "true") return;

        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        if (this.initialized) {
          this.refreshAddButtons();
          this.evaluateCart();
          return;
        }
        this.initialized = true;

        this.closeButtons.forEach((button) => {
          button.addEventListener("click", () => this.dismiss());
        });

        if (this.overlay) {
          this.overlay.classList.add("quiz-promotion-cards");
          this.overlay.addEventListener("click", (event) => {
            if (event.target === this.overlay) this.dismiss();
          });
          this.overlay.addEventListener("keydown", (event) => {
            if (event.key === "Escape" && this.isOpen) this.dismiss();
          });
        }

        this.refreshAddButtons();
        this.addButtons.forEach((button) => {
          button.addEventListener("click", (event) => this.onAddToCart(event));
        });

        if (this.teaserReopen) {
          this.teaserReopen.addEventListener("click", () => {
            this.hideTeaser();
            this.open();
          });
        }

        if (this.teaserDismiss) {
          this.teaserDismiss.addEventListener("click", () => this.dismissTeaserPermanently());
        }

        this.addEventListener("keydown", (event) => {
          if (event.key === "Escape" && this.isOpen) this.dismiss();
        });

        this.installCartWatcher();
        this.installGiftQtyObserver();
        this.installDesignModeWatcher();

        if (this.dataset.designMode === "true") return;

        this.evaluateCart();
      }

      installDesignModeWatcher() {
        if (this.dataset.designMode !== "true") return;

        const sectionEl = this.closest(".shopify-section");
        document.addEventListener("shopify:section:select", (event) => {
          if (event.target === sectionEl) this.open();
        });
        document.addEventListener("shopify:section:deselect", (event) => {
          if (event.target === sectionEl) this.close();
        });
        document.addEventListener("shopify:section:load", (event) => {
          if (event.target === sectionEl) this.open();
        });
      }

      refreshAddButtons() {
        this.addButtons = this.querySelectorAll("[data-quiz-promotion-add]");
        return this.addButtons;
      }

      installCartWatcher() {
        if (!window.__quizPromotionFetchPatched) {
          const originalFetch = window.fetch.bind(window);
          window.fetch = (...args) => {
            return originalFetch(...args).then((response) => {
              const url = this.getRequestUrl(args[0]);
              if (this.isCartMutationUrl(url)) {
                document.dispatchEvent(new CustomEvent("quiz-promotion:cart-changed"));
              }
              return response;
            });
          };
          window.__quizPromotionFetchPatched = true;
        }

        if (!window.__quizPromotionXhrPatched) {
          const originalOpen = XMLHttpRequest.prototype.open;
          XMLHttpRequest.prototype.open = function (method, url, ...rest) {
            this.addEventListener("load", () => {
              try {
                const path = typeof url === "string" ? url : "";
                if (/\/cart\/(add|change|update|clear)/.test(path)) {
                  document.dispatchEvent(new CustomEvent("quiz-promotion:cart-changed"));
                }
              } catch (error) {
                /* ignore invalid XHR urls */
              }
            });
            return originalOpen.call(this, method, url, ...rest);
          };
          window.__quizPromotionXhrPatched = true;
        }

        document.addEventListener("quiz-promotion:cart-changed", () => {
          this.scheduleEvaluate({ fromCartChange: true });
        });

        [
          "rebuy:cart.change",
          "rebuy.ready",
          "rebuy.addToCart",
          "rebuy:smartcart.ready",
          "rebuy:smartcart.show",
        ].forEach((eventName) => {
          const fromCartChange = eventName === "rebuy:cart.change" || eventName === "rebuy.addToCart";
          document.addEventListener(eventName, () => {
            this.scheduleEvaluate({ fromCartChange });
            setTimeout(() => {
              this.fetchCart()
                .then((cart) => this.lockGiftQuantityControls(cart))
                .catch(() => {});
            }, 300);
          });
        });

        const bindPubSub = () => {
          if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return false;
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event?.source === "quiz-promotion") return;
            this.scheduleEvaluate({ fromCartChange: true });
          });
          return true;
        };

        if (!bindPubSub()) {
          const interval = setInterval(() => {
            if (bindPubSub()) clearInterval(interval);
          }, 200);
          setTimeout(() => clearInterval(interval), 10000);
        }
      }

      getRequestUrl(input) {
        if (typeof input === "string") return input;
        if (input instanceof URL) return input.href;
        if (input && typeof input.url === "string") return input.url;
        return "";
      }

      isCartMutationUrl(url) {
        if (!url) return false;
        try {
          const path = new URL(url, window.location.origin).pathname;
          return /\/cart\/(add|change|update|clear)/.test(path);
        } catch (error) {
          return /\/cart\/(add|change|update|clear)/.test(String(url));
        }
      }

      scheduleEvaluate(options = {}) {
        if (options.fromCartChange) this.pendingCartChange = true;
        clearTimeout(this.evaluateTimer);
        this.evaluateTimer = setTimeout(() => this.evaluateCart(), 350);
      }

      async fetchCart() {
        const root = window.Shopify?.routes?.root || "/";
        const response = await fetch(`${root}cart.js`, { credentials: "same-origin" });
        if (!response.ok) throw new Error("Unable to load cart");
        const cart = await response.json();
        return this.withProductTags(cart);
      }

      async getProductTags(item) {
        const handle = item?.handle;
        if (!handle) return [];
        if (Object.prototype.hasOwnProperty.call(this.productTagsCache, handle)) {
          return this.productTagsCache[handle];
        }

        const root = window.Shopify?.routes?.root || "/";
        try {
          const response = await fetch(`${root}products/${handle}.js`, { credentials: "same-origin" });
          if (!response.ok) {
            this.productTagsCache[handle] = [];
            return [];
          }
          const product = await response.json();
          const tags = Array.isArray(product.tags) ? product.tags : [];
          this.productTagsCache[handle] = tags;
          return tags;
        } catch (error) {
          this.productTagsCache[handle] = [];
          return [];
        }
      }

      async withProductTags(cart) {
        const items = cart?.items || [];
        const uniqueHandles = [...new Set(items.map((item) => item.handle).filter(Boolean))];
        await Promise.all(uniqueHandles.map((handle) => this.getProductTags({ handle })));
        items.forEach((item) => {
          item.tags = this.productTagsCache[item.handle] || [];
        });
        return cart;
      }

      hasProductTag(item, tag) {
        const tags = item?.tags;
        if (!Array.isArray(tags) || !tag) return false;
        const needle = String(tag).toLowerCase();
        return tags.some((value) => String(value).toLowerCase() === needle);
      }

      hasQuizProperty(item) {
        return this.isTruthyProperty(this.getLineProperty(item, QuizPromotion.QUIZ_PROPERTY));
      }

      hasGiftProperty(item) {
        const giftFlag = this.getLineProperty(item, QuizPromotion.GIFT_FLAG_PROPERTY);
        return giftFlag != null && String(giftFlag).trim() !== "";
      }

      isTruthyProperty(value) {
        if (value === true || value === 1) return true;
        if (typeof value !== "string") return false;
        const normalized = value.toLowerCase();
        return normalized === "true" || normalized === "1" || normalized === "yes";
      }

      getLineProperty(item, key) {
        const props = item?.properties;
        if (!props) return null;

        if (Array.isArray(props)) {
          const match = props.find((prop) => {
            if (!prop) return false;
            if (prop.name === key || prop.first === key) return true;
            if (Array.isArray(prop) && prop[0] === key) return true;
            return false;
          });
          if (!match) return null;
          return match.value ?? match.last ?? match[1] ?? null;
        }

        if (typeof props === "object") {
          if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];
          const foundKey = Object.keys(props).find((propKey) => propKey.toLowerCase() === key.toLowerCase());
          return foundKey ? props[foundKey] : null;
        }

        return null;
      }

      isFreeGiftItem(item) {
        return this.hasGiftProperty(item) && this.hasProductTag(item, QuizPromotion.GIFT_TAG);
      }

      isQuizProductItem(item) {
        if (this.isFreeGiftItem(item)) return false;
        return this.hasQuizProperty(item) && this.hasProductTag(item, QuizPromotion.QUIZ_TAG);
      }

      countQuizProducts(cart) {
        return (cart.items || []).reduce((total, item) => {
          if (!this.isQuizProductItem(item)) return total;
          return total + (item.quantity || 0);
        }, 0);
      }

      getFreeProductItems(cart) {
        return (cart.items || []).filter((item) => this.isFreeGiftItem(item));
      }

      getMaxFreeProducts() {
        const raw = (this.dataset.maxFreeProducts || "unlimited").toLowerCase();
        if (raw === "unlimited" || raw === "" || raw === "0") return Infinity;
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : Infinity;
      }

      getEntitledFreeCount(quizCount) {
        const byRatio = Math.floor((quizCount || 0) / QuizPromotion.MIN_QUIZ_PRODUCTS);
        return Math.min(byRatio, this.getMaxFreeProducts());
      }

      isDismissed(entitledCount) {
        try {
          const stored = sessionStorage.getItem(QuizPromotion.STORAGE_KEY);
          if (!stored) return false;
          if (stored === "true") return (entitledCount || 0) <= 1;
          const dismissedAt = parseInt(stored, 10);
          if (Number.isNaN(dismissedAt)) return false;
          return (entitledCount || 0) <= dismissedAt;
        } catch (error) {
          return false;
        }
      }

      setDismissed(entitledCount) {
        try {
          sessionStorage.setItem(QuizPromotion.STORAGE_KEY, String(entitledCount || 1));
        } catch (error) {
          /* sessionStorage can be unavailable */
        }
      }

      getLastEntitledCount() {
        if (this.lastEntitledCount) return this.lastEntitledCount;
        try {
          const stored = parseInt(sessionStorage.getItem(QuizPromotion.ENTITLED_KEY), 10);
          return Number.isFinite(stored) && stored > 0 ? stored : 0;
        } catch (error) {
          return 0;
        }
      }

      setLastEntitledCount(count) {
        this.lastEntitledCount = count || 0;
        try {
          if (this.lastEntitledCount) {
            sessionStorage.setItem(QuizPromotion.ENTITLED_KEY, String(this.lastEntitledCount));
          } else {
            sessionStorage.removeItem(QuizPromotion.ENTITLED_KEY);
          }
        } catch (error) {
          /* sessionStorage can be unavailable */
        }
      }

      clearDismissed() {
        try {
          sessionStorage.removeItem(QuizPromotion.STORAGE_KEY);
        } catch (error) {
          /* sessionStorage can be unavailable */
        }
      }

      isPromptPermanentlyDismissed() {
        try {
          return localStorage.getItem(QuizPromotion.PROMPT_DISMISS_KEY) === "true";
        } catch (error) {
          return false;
        }
      }

      setPromptPermanentlyDismissed() {
        try {
          localStorage.setItem(QuizPromotion.PROMPT_DISMISS_KEY, "true");
        } catch (error) {
          /* localStorage can be unavailable */
        }
      }

      isTeaserVisible() {
        return Boolean(this.teaser && !this.teaser.hidden);
      }

      updateRootVisibility() {
        if (this.isOpen || this.isTeaserVisible()) {
          this.hidden = false;
          this.setAttribute("aria-hidden", "false");
          return;
        }
        this.hidden = true;
        this.setAttribute("aria-hidden", "true");
      }

      showTeaser() {
        if (this.dataset.designMode === "true") return;
        if (this.isOpen || this.isPromptPermanentlyDismissed()) return;
        if (!this.teaser || !this.refreshAddButtons().length) return;
        this.teaser.hidden = false;
        this.teaser.setAttribute("aria-hidden", "false");
        this.updateRootVisibility();
      }

      hideTeaser() {
        if (!this.teaser) return;
        this.teaser.hidden = true;
        this.teaser.setAttribute("aria-hidden", "true");
        this.updateRootVisibility();
      }

      dismissTeaserPermanently() {
        this.setPromptPermanentlyDismissed();
        this.hideTeaser();
      }

      async evaluateCart() {
        if (this.dataset.designMode === "true" || this.isBusy || this.removingFree || this.lockingQty) return;

        const fromCartChange = this.pendingCartChange;
        this.pendingCartChange = false;

        try {
          const cart = await this.fetchCart();
          this.lastCart = cart;
          const quizCount = this.countQuizProducts(cart);
          const freeItems = this.getFreeProductItems(cart);
          const entitledCount = this.getEntitledFreeCount(quizCount);
          const previousEntitled = this.getLastEntitledCount();
          const reachedNewThreshold = fromCartChange && entitledCount > previousEntitled;

          if (entitledCount < 1) {
            this.clearDismissed();
            this.setLastEntitledCount(0);
            if (this.isOpen) this.close();
            this.hideTeaser();
            if (freeItems.length) {
              await this.removeFreeProducts(freeItems);
            }
            return;
          }

          if (freeItems.length > entitledCount) {
            await this.removeFreeProducts(freeItems.slice(entitledCount));
            return;
          }

          await this.enforceFreeProductQuantity(freeItems);
          this.lockGiftQuantityControls(cart);

          if (freeItems.length >= entitledCount) {
            this.setLastEntitledCount(entitledCount);
            if (this.isOpen) this.close();
            this.hideTeaser();
            return;
          }

          this.setLastEntitledCount(entitledCount);

          if (!this.refreshAddButtons().length) return;

          if (reachedNewThreshold) {
            this.clearDismissed();
            if (this.isOpen) return;
            this.open();
            return;
          }

          if (this.isOpen) return;

          this.showTeaser();
        } catch (error) {
          console.error("Quiz promotion:", error);
        }
      }

      async enforceFreeProductQuantity(items) {
        const overflowItems = (items || []).filter((item) => (item.quantity || 0) !== 1);
        if (!overflowItems.length || this.lockingQty) return;

        this.lockingQty = true;
        try {
          for (const item of overflowItems) {
            await this.changeLineQuantity(item, 1);
          }
        } finally {
          this.lockingQty = false;
        }
      }

      hasGiftPropertyMarker(root) {
        if (!root) return false;
        const marker = "_quiz_promotional_product";
        const className = `${root.className || ""} ${root.getAttribute("class") || ""}`;
        if (className.includes(marker)) return true;

        return Array.from(root.querySelectorAll(`[class*="${marker}"]`)).some((node) => {
          return node.closest(".rebuy-cart__flyout-item, .cart-item") === root;
        });
      }

      isGiftControlRoot(root, cart) {
        if (!root) return false;
        if (this.hasGiftPropertyMarker(root)) return true;

        const giftItems = this.getFreeProductItems(cart);
        if (!giftItems.length) return false;

        const key =
          root.dataset?.itemKey ||
          root.dataset?.rebuyItemKey ||
          root.dataset?.key ||
          root.getAttribute("data-item-key") ||
          root.getAttribute("data-key");
        if (key && giftItems.some((item) => item.key === key || item.key === key.replace(/&amp;/g, "&"))) {
          return true;
        }

        return false;
      }

      installGiftQtyObserver() {
        if (this.giftQtyObserver || typeof MutationObserver === "undefined") return;

        this.giftQtyObserver = new MutationObserver(() => {
          clearTimeout(this.giftQtyLockTimer);
          this.giftQtyLockTimer = setTimeout(() => {
            const cart = this.lastCart;
            if (cart) {
              this.lockGiftQuantityControls(cart);
              return;
            }
            this.fetchCart()
              .then((nextCart) => this.lockGiftQuantityControls(nextCart))
              .catch(() => {});
          }, 150);
        });

        const observeTarget = () => {
          const rebuyCart = document.querySelector(".rebuy-cart, [data-rebuy-component='cart']");
          if (!rebuyCart) return;
          this.giftQtyObserver.observe(rebuyCart, { childList: true, subtree: true });
        };

        observeTarget();
        document.addEventListener("rebuy:smartcart.ready", observeTarget, { once: true });
      }

      lockGiftQuantityControls(cart) {
        const itemSelectors = [
          ".rebuy-cart__flyout-item",
          "[data-rebuy-component='cart-items'] .rebuy-cart__flyout-item",
          ".cart-item",
        ];
        document.querySelectorAll(itemSelectors.join(",")).forEach((root) => {
          const shouldLock =
            root.classList.contains("cart-item--disable-quantity") || this.isGiftControlRoot(root, cart);

          root.querySelectorAll("button, input").forEach((control) => {
            if (control.closest("cart-remove-button, .rebuy-cart__flyout-item-remove, [class*='item-remove']")) {
              return;
            }
            if (
              !control.closest(
                ".quantity, .cart-quantity, .rebuy-cart__flyout-item-quantity, .rebuy-cart__flyout-item-quantity-widget"
              )
            ) {
              return;
            }

            if (!shouldLock) {
              if (!control.dataset.quizPromoLocked) return;
              control.disabled = false;
              control.removeAttribute("aria-disabled");
              if (control.tagName === "INPUT") control.readOnly = false;
              delete control.dataset.quizPromoLocked;
              return;
            }

            control.disabled = true;
            control.setAttribute("aria-disabled", "true");
            control.dataset.quizPromoLocked = "true";
            if (control.tagName === "INPUT") {
              control.readOnly = true;
              control.value = "1";
            }
          });

          root.classList.toggle("quiz-promotion-qty-locked", shouldLock);
          this.toggleGiftSubscriptionUpgrade(root, shouldLock);
        });
      }

      toggleGiftSubscriptionUpgrade(root, hide) {
        if (!root) return;
        root.querySelectorAll(".rebuy-cart__flyout-item-subscription").forEach((node) => {
          node.hidden = hide;
          node.style.setProperty("display", hide ? "none" : "", "important");
        });
      }

      async removeFreeProducts(items) {
        if (this.removingFree || !items.length) return;
        this.removingFree = true;

        try {
          for (const item of items) {
            await this.changeLineQuantity(item, 0);
          }
        } finally {
          this.removingFree = false;
        }
      }

      getCartElement() {
        return document.querySelector("cart-drawer") || document.querySelector("cart-notification");
      }

      getCartSectionIds() {
        const cart = this.getCartElement();
        if (cart && typeof cart.getSectionsToRender === "function") {
          return cart.getSectionsToRender().map((section) => section.id);
        }
        if (document.querySelector("cart-drawer")) {
          return ["cart-drawer", "cart-icon-bubble"];
        }
        return ["cart-notification-product", "cart-notification-button", "cart-icon-bubble"];
      }

      getSectionsPayload() {
        return this.getCartSectionIds();
      }

      async ensureCartReady() {
        this.cart = this.getCartElement();
        if (this.cart && typeof this.cart.renderContents === "function") return this.cart;

        if (typeof customElements.whenDefined === "function" && !customElements.get("cart-drawer")) {
          try {
            await Promise.race([
              customElements.whenDefined("cart-drawer"),
              new Promise((resolve) => setTimeout(resolve, 3000)),
            ]);
          } catch (error) {
            /* cart-drawer script may still be loading */
          }
        }

        this.cart = this.getCartElement();
        return this.cart;
      }

      closeCartDrawer() {
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) {
          if (typeof cartDrawer.close === "function") {
            cartDrawer.close();
          }
          cartDrawer.classList.remove("active", "animate");
        }

        if (window.Rebuy?.SmartCart?.hide) {
          window.Rebuy.SmartCart.hide();
        }
      }

      openCartAfterAdd(parsedState) {
        const cart = this.getCartElement();
        this.cart = cart;

        if (document.body.classList.contains("template-cart")) {
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "quiz-promotion" });
          }
          return;
        }

        if (document.body.classList.contains("smart-cart--enabled") && window.Rebuy?.SmartCart?.show) {
          window.Rebuy.SmartCart.show();
          return;
        }

        if (!cart) return;

        cart.classList.remove("is-empty");

        if (typeof cart.renderContents === "function") {
          cart.renderContents(parsedState);
          return;
        }

        if (parsedState?.sections) {
          this.refreshCartUi(parsedState, { openCart: false });
        }

        if (typeof cart.open === "function") {
          cart.open();
          return;
        }

        cart.classList.add("animate", "active");
        document.body.classList.add("overflow-hidden");
      }

      async changeLineQuantity(item, quantity) {
        const body = {
          id: item.key,
          quantity,
        };
        const sections = this.getSectionsPayload();
        if (sections) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }

        const changeUrl = window.routes?.cart_change_url || "/cart/change.js";
        const response = await fetch(changeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });
        const state = await response.json();
        this.refreshCartUi(state, { openCart: false });

        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "quiz-promotion" });
        }
      }

      refreshCartUi(parsedState, { openCart = false } = {}) {
        if (!parsedState || parsedState.errors) return;

        if (this.cart && typeof this.cart.renderContents === "function" && parsedState.sections) {
          if (openCart && !this.cart.shouldSuppressCartDrawer?.()) {
            this.cart.renderContents(parsedState);
            return;
          }

          this.cart.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!sectionElement || !parsedState.sections[section.id]) return;
            sectionElement.innerHTML = this.cart.getSectionInnerHTML(
              parsedState.sections[section.id],
              section.selector
            );
          });

          this.cart.classList.toggle("is-empty", parsedState.item_count === 0);
          const overlay = this.cart.querySelector("#CartDrawer-Overlay");
          if (overlay && typeof w3elem !== "undefined") {
            overlay.addEventListener(w3elem, this.cart.close.bind(this.cart));
          }
          return;
        }

        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "quiz-promotion" });
        }
      }

      open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.hideTeaser();
        this.resetAddButtons();
        this.hidden = false;
        this.classList.add("is-open");
        if (this.overlay) this.overlay.classList.add("is-open");
        this.closeCartDrawer();
        this.lockPageScroll();

        this.setAttribute("aria-hidden", "false");
        if (this.dialog) {
          this.dialog.setAttribute("aria-hidden", "false");
          if (typeof trapFocus === "function") {
            trapFocus(this.dialog, this.querySelector("[data-quiz-promotion-close]"));
          } else {
            this.querySelector("[data-quiz-promotion-close]")?.focus();
          }
        }
      }

      close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.classList.remove("is-open");
        if (this.overlay) this.overlay.classList.remove("is-open");
        if (this.dialog) this.dialog.setAttribute("aria-hidden", "true");
        this.unlockPageScroll();
        if (typeof removeTrapFocus === "function") removeTrapFocus();
        this.updateRootVisibility();
      }

      lockPageScroll() {
        this.scrollY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.classList.add("quiz-promotion-lock");
        document.body.classList.add("quiz-promotion-lock");
        document.body.style.top = `-${this.scrollY}px`;
      }

      unlockPageScroll() {
        document.documentElement.classList.remove("quiz-promotion-lock");
        document.body.classList.remove("quiz-promotion-lock");
        document.body.style.top = "";
        window.scrollTo(0, this.scrollY || 0);
      }

      dismiss() {
        if (this.dataset.designMode !== "true") {
          const quizCount = this.countQuizProducts(this.lastCart || { items: [] });
          const entitledCount = this.lastEntitledCount || this.getEntitledFreeCount(quizCount);
          this.setDismissed(entitledCount);
        }
        this.close();
        this.showTeaser();
      }

      resetAddButtons() {
        this.isBusy = false;
        this.addButtons.forEach((button) => {
          button.classList.remove("is-loading");
          button.removeAttribute("aria-disabled");
          button.disabled = button.dataset.available === "false";
        });
      }

      setButtonsDisabled(disabled, exceptButton) {
        this.addButtons.forEach((button) => {
          if (disabled && exceptButton && button === exceptButton) return;
          button.disabled = disabled || button.dataset.available === "false";
        });
      }

      async onAddToCart(event) {
        const button = event.currentTarget;
        const variantId = button.dataset.variantId;
        if (!variantId || this.isBusy || button.disabled) return;

        this.isBusy = true;
        this.setButtonsDisabled(true, button);
        button.classList.add("is-loading");
        button.setAttribute("aria-disabled", "true");

        try {
          const currentCart = await this.fetchCart();
          const quizCount = this.countQuizProducts(currentCart);
          const entitledCount = this.getEntitledFreeCount(quizCount);
          const freeCount = this.getFreeProductItems(currentCart).length;

          if (entitledCount < 1) {
            this.resetAddButtons();
            this.hideTeaser();
            this.close();
            return;
          }
          if (freeCount >= entitledCount) {
            this.resetAddButtons();
            this.setDismissed(entitledCount);
            this.hideTeaser();
            this.close();
            return;
          }
          const formData = new FormData();
          formData.append("id", variantId);
          formData.append("quantity", 1);
          formData.append(`properties[${QuizPromotion.GIFT_FLAG_PROPERTY}]`, "true");

          const cart = await this.ensureCartReady();
          formData.append("sections", this.getCartSectionIds().join(","));
          formData.append("sections_url", window.location.pathname);
          if (cart && typeof cart.setActiveElement === "function") {
            cart.setActiveElement(document.activeElement);
          }

          const addUrl = window.routes?.cart_add_url || "/cart/add.js";
          const response = await fetch(addUrl, {
            method: "POST",
            headers: {
              "X-Requested-With": "XMLHttpRequest",
              Accept: "application/javascript",
            },
            body: formData,
          });
          const parsedState = await response.json();

          if (parsedState.status) {
            throw new Error(parsedState.description || parsedState.message || "Unable to add product");
          }

          if (freeCount + 1 >= entitledCount) {
            this.setDismissed(entitledCount);
            this.hideTeaser();
          }
          this.close();
          await this.ensureCartReady();
          this.openCartAfterAdd(parsedState);
        } catch (error) {
          console.error("Quiz promotion add:", error);
          this.resetAddButtons();
        } finally {
          button.classList.remove("is-loading");
          button.removeAttribute("aria-disabled");
          this.isBusy = false;
        }
      }
    }
  );
}
